import { styled, css, useTheme } from '@vebox/etui-system';
import { useEffect, createElement } from 'react';
import { _ as _objectWithoutProperties, a as _objectSpread2, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Div } from '@vebox/etui-system-elements';
import { H5 } from '../H5/H5.js';
import '../Svg-0180d7b5.js';
import Icon from '../Icon/Icon.js';
import '@vebox/etui-icons';
import { icons } from '../Icon/icons.js';
import '../Icon/helper.js';
import { focusNextSiblingOf, focusPreviousSiblingOf, focusLastSiblingOf, focusFirstSiblingOf } from './utils/focus.js';

const _excluded = ["theme", "isOpen"];
const AccordionSectionDiv = styled(Div).attrs(_ref => {
  let {
      theme,
      isOpen
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  return _objectSpread2({
    bg: 'inherit',
    color: theme.palette.text.primary,
    borderTop: `1px solid ${theme.palette.divider}`
  }, rest);
})`
  &:first-of-type {
    border-top: 0;
  }
`;

const AccordionSectionHeaderDiv = styled(Div).attrs(({
  theme
}) => ({
  tabIndex: 0,
  fontSize: theme.typography.fontSizeLarge
}))`
  cursor: pointer;
  user-select: none;
  width: 100%;
  transition: 0.2s;

  ${({
  theme
}) => {
  return css`
      &:focus-visible,
      &:hover {
        outline: 0;
        background-color: ${theme.palette.action.hover};
      }
    `;
}};

  ${({
  theme,
  isOpen
}) => {
  if (isOpen) {
    return css`
        background-color: ${theme.palette.action.hover};
      `;
  }
  return ``;
}};
`;

const AccordionSectionNameSpan = styled(Div).attrs(({
  theme
}) => ({
  p: `${theme.spacing.sm}px ${theme.spacing.sm}px ${theme.spacing.sm}px 0`
}))`
  flex-grow: 1;
  background-color: transparent;
  transition: 0.2s;
`;

const _excluded$1 = ["theme"];
const AccordionSectionContentDiv = styled(Div).attrs(_ref => {
  let {
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$1);
  return _objectSpread2({
    p: theme.spacing.md
  }, props);
})``;

const _excluded$2 = ["theme"];
const AccordionSectionFarItemWrapper = styled(Div).attrs(_ref => {
  let {
      theme
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded$2);
  return _objectSpread2({
    pr: `${theme.spacing.sm}px`
  }, rest);
})`
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  height: fit-content;
  max-height: 100%;
  overflow: hidden;
  flex-shrink: 0;
  max-width: calc(100% - 100px);
`;

const keyCodes = {
  DOWN: '40',
  END: '35',
  ENTER: '13',
  HOME: '36',
  LEFT: '37',
  RIGHT: '39',
  SPACE: '32',
  UP: '38'
};
const AccordionSection = ({
  onOpen = null,
  onClose = null,
  onToggle = null,
  styles = {},
  isOpen,
  name,
  children,
  renderHeader,
  keepSectionContentInDOM,
  itemProps,
  renderHeaderFarItem
}) => {
  var _styles$header, _styles$header2, _styles$header2$expan, _styles$header3, _styles$header3$expan, _styles$header4, _styles$header4$title, _styles$header5, _styles$header5$title, _styles$header6, _styles$sectionConten;
  const theme = useTheme();
  useEffect(() => {
    if (onOpen && isOpen) {
      onOpen(itemProps);
    }
    if (onClose && !isOpen) {
      onClose(itemProps);
    }
  }, [onOpen, onClose, isOpen, itemProps]);
  const handleClick = () => {
    if (onToggle) {
      onToggle(name);
    }
  };
  const handleKeyPress = evt => {
    const keyCode = evt.which.toString();
    if (keyCode === keyCodes.ENTER || keyCode === keyCodes.SPACE) {
      evt.preventDefault();
      handleClick();
    }
    if (evt.target instanceof HTMLElement) {
      switch (keyCode) {
        case keyCodes.HOME:
          {
            evt.preventDefault();
            focusFirstSiblingOf(evt.target);
            break;
          }
        case keyCodes.END:
          {
            evt.preventDefault();
            focusLastSiblingOf(evt.target);
            break;
          }
        case keyCodes.LEFT:
        case keyCodes.UP:
          {
            evt.preventDefault();
            focusPreviousSiblingOf(evt.target);
            break;
          }
        case keyCodes.RIGHT:
        case keyCodes.DOWN:
          {
            evt.preventDefault();
            focusNextSiblingOf(evt.target);
            break;
          }
      }
    }
  };
  return /*#__PURE__*/createElement(AccordionSectionDiv, _extends({
    isOpen: isOpen
  }, styles.root), /*#__PURE__*/createElement(AccordionSectionHeaderDiv, _extends({
    onClick: handleClick,
    onKeyDown: handleKeyPress,
    isOpen: isOpen,
    "data-component": "AccordionSectionHeader"
  }, styles.sectionHeader, (_styles$header = styles.header) === null || _styles$header === void 0 ? void 0 : _styles$header.root), renderHeader ? renderHeader(isOpen) : /*#__PURE__*/createElement(Div, {
    display: "flex",
    justifyContent: "space-between",
    width: "100%"
  }, /*#__PURE__*/createElement(Div, {
    display: "flex",
    alignItems: "center",
    width: "100%"
  }, /*#__PURE__*/createElement(Div, _extends({
    display: "flex",
    alignItems: "center",
    p: `${theme.spacing.sm}px`,
    color: isOpen ? theme.palette.text.primary : theme.palette.text.secondary
  }, (_styles$header2 = styles.header) === null || _styles$header2 === void 0 ? void 0 : (_styles$header2$expan = _styles$header2.expander) === null || _styles$header2$expan === void 0 ? void 0 : _styles$header2$expan.wrapper), /*#__PURE__*/createElement(Icon, {
    glyph: icons.CHEVRON_DOWN,
    rotate: isOpen ? 0 : -90,
    size: 24,
    styles: _objectSpread2({
      root: {
        transition: '0.2s'
      }
    }, (_styles$header3 = styles.header) === null || _styles$header3 === void 0 ? void 0 : (_styles$header3$expan = _styles$header3.expander) === null || _styles$header3$expan === void 0 ? void 0 : _styles$header3$expan.icon)
  })), /*#__PURE__*/createElement(AccordionSectionNameSpan, (_styles$header4 = styles.header) === null || _styles$header4 === void 0 ? void 0 : (_styles$header4$title = _styles$header4.title) === null || _styles$header4$title === void 0 ? void 0 : _styles$header4$title.wrapper, /*#__PURE__*/createElement(H5, _extends({
    color: isOpen ? theme.palette.text.primary : theme.palette.text.secondary
  }, (_styles$header5 = styles.header) === null || _styles$header5 === void 0 ? void 0 : (_styles$header5$title = _styles$header5.title) === null || _styles$header5$title === void 0 ? void 0 : _styles$header5$title.text), name)), typeof renderHeaderFarItem === 'function' && /*#__PURE__*/createElement(AccordionSectionFarItemWrapper, (_styles$header6 = styles.header) === null || _styles$header6 === void 0 ? void 0 : _styles$header6.farItem, renderHeaderFarItem())))), (isOpen || keepSectionContentInDOM) && /*#__PURE__*/createElement(AccordionSectionContentDiv, _extends({}, styles.sectionContent, {
    display: keepSectionContentInDOM && !isOpen ? 'none' : ((_styles$sectionConten = styles.sectionContent) === null || _styles$sectionConten === void 0 ? void 0 : _styles$sectionConten.display) || 'block'
  }), children));
};

export default AccordionSection;
